import type { AutomergeUrl } from "@automerge/automerge-repo";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { History, X } from "lucide-react";
import { lazy, Suspense } from "react";

import { PendingPane } from "../../-pending";
import { basename } from "../../-route-title";
import { useWorkspace } from "../../-workspace-context";
import { useResolvedFile } from "../../../automerge/project-tree";
import { TitleBar } from "../../../components/TitleBar";
import { EditorTabs } from "./-editor-tabs";
import { ShareButton } from "./-share-button";

// Markdown editor is the special case (wiki-links, prose). Anything else
// — TypeScript, plain text, JSON, YAML — falls through to Monaco, which
// handles any text content. Both kinds share the same document tabs; the
// editor is swapped per focused tab.
const MARKDOWN_EXTS = [".md", ".markdown"];

// Each editor ships in its own chunk so opening a markdown file doesn't
// pull in Monaco (and vice versa).
const MarkdownView = lazy(() => import("./-markdown-view"));
const MonacoView = lazy(() => import("./-monaco-view"));

export const Route = createFileRoute("/_workspace/files/$")({
  head: ({ params }) => {
    const path = params._splat ?? "";
    return { meta: [{ title: path ? basename(path) : "Files" }] };
  },
  component: FilesPage,
});

function FilesPage() {
  const {
    author,
    playground,
    openFile,
    closeFile,
    editorTabs,
    closeEditorTab,
    closeAllEditorTabs,
  } = useWorkspace();
  const navigate = useNavigate();
  // `_splat` is TanStack's canonical splat param name.
  const { _splat } = Route.useParams();
  const rawPath = _splat ?? "";

  // A wiki-link's short form (`[[Justin]]` → `/files/Justin`) omits the `.md`
  // extension and may differ in case. The chat UI resolves these before
  // navigating; an external entry (an email link, a bookmark) doesn't, so a raw
  // path that misses resolves here too. A genuine miss keeps the raw path.
  const resolved = useResolvedFile(playground, rawPath);
  const path = resolved?.path ?? rawPath;
  const inode = rawPath ? resolved?.inode : null;
  // Editable docs are Automerge-backed. A mounted (managed) file isn't in the
  // doc, so it has no automerge url — Monaco opens it through the FS and goes
  // read-only on its own (it's a blob, not a writable doc), same as a real FS
  // you just can't write to. Only sharing needs a doc url.
  const url =
    inode && inode.uri.startsWith("automerge:")
      ? (inode.uri as AutomergeUrl)
      : null;

  const isMarkdown = MARKDOWN_EXTS.some((ext) =>
    path.toLowerCase().endsWith(ext),
  );
  const showTabs = path !== "";

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TitleBar
        actions={
          <>
            {/* Only an editable markdown doc is shareable — mounted/managed
                files and code stay workspace-only. */}
            {isMarkdown && url ? <ShareButton path={path} /> : null}
            <button
              type="button"
              onClick={() =>
                navigate({ to: "/history/$", params: { _splat: path } })
              }
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              title="History"
              aria-label="Open document history"
            >
              <History className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={showTabs ? closeAllEditorTabs : closeFile}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              title={showTabs ? "Close all tabs" : "Close"}
              aria-label={showTabs ? "Close editor" : "Close file"}
            >
              <X className="h-4 w-4" />
            </button>
          </>
        }
      >
        {showTabs ? (
          <EditorTabs
            tabs={editorTabs}
            activePath={path}
            onSelect={openFile}
            onClose={closeEditorTab}
          />
        ) : (
          <span className="truncate text-sm font-medium">{path}</span>
        )}
      </TitleBar>
      <Suspense fallback={<PendingPane />}>
        {inode === undefined ? (
          <PendingPane />
        ) : isMarkdown ? (
          // Markdown → the rich editor, editable when there's a doc and
          // read-only (from the FS) when it's mounted/managed.
          <MarkdownView
            path={path}
            playground={playground}
            author={author}
            onOpenFile={openFile}
          />
        ) : (
          // Everything else → Monaco, likewise editable or read-only by
          // writability. The editor is chosen by file type, never by mount.
          <MonacoView path={path} playground={playground} author={author} />
        )}
      </Suspense>
    </div>
  );
}
